// The sheets that make and change a car: adding one (its name and what it is), its details as the TUNE MFD shows them,
// and the figures only the Analyser uses. Each checks what's typed before it's sent, and says what's wrong beside it.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@tune/shared';
import { useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { data, problem } from '../../api';
import { useAuth } from '../../auth';
import { FUELS } from '../../lib/car';
import { useIdempotencyKey } from '../../lib/idempotency';
import { keys } from '../../queries';
import { Banner, Select, Sheet, TextArea, TextField, useToast } from '../../ui';
import s from './Garage.module.css';

type Vehicle = components['schemas']['Vehicle'];
type VehicleIn = components['schemas']['VehicleIn'];
type Patch = components['schemas']['VehiclePatch'];
type Fuel = components['schemas']['Fuel'];

/** A car's details as they're typed. */
interface Details {
  nickname: string;
  make: string;
  model: string;
  year: string;
  engine: string;
  transmission: string;
  bio: string;
}

type Errors = Partial<Record<keyof Details, string>>;

/** A car's details, as the API takes them. */
type DetailsBody = Pick<VehicleIn, 'nickname' | 'make' | 'model' | 'year' | 'engine' | 'transmission' | 'bio'>;

const NONE: Details = { nickname: '', make: '', model: '', year: '', engine: '', transmission: '', bio: '' };

function detailsOf(car: Vehicle): Details {
  return {
    nickname: car.nickname,
    make: car.make ?? '',
    model: car.model ?? '',
    year: car.year === null ? '' : String(car.year),
    engine: car.engine ?? '',
    transmission: car.transmission ?? '',
    bio: car.bio ?? '',
  };
}

/** What's typed, as the API takes it, or what's wrong with it. */
function readDetails(form: Details): { body: DetailsBody } | { errors: Errors } {
  const errors: Errors = {};
  if (!form.nickname.trim()) {
    errors.nickname = 'Give the car a name.';
  }
  const year = form.year.trim() === '' ? null : Number(form.year.trim());
  if (year !== null && (!Number.isInteger(year) || year < 1886 || year > 2100)) {
    errors.year = 'A year from 1886 to 2100.';
  }
  if (Object.keys(errors).length > 0) {
    return { errors };
  }
  const text = (value: string) => (value.trim() === '' ? null : value.trim());
  return {
    body: {
      nickname: form.nickname.trim(),
      make: text(form.make),
      model: text(form.model),
      year,
      engine: text(form.engine),
      transmission: text(form.transmission),
      bio: text(form.bio),
    },
  };
}

/** The fields for a car's details: its name and what it is, and with `full`, its engine, gearbox and a line about it. */
function DetailFields({
  form,
  set,
  errors,
  full,
}: {
  form: Details;
  set: (key: keyof Details) => (e: { target: { value: string } }) => void;
  errors: Errors;
  full: boolean;
}) {
  return (
    <>
      <TextField
        label="Name"
        hint={full ? undefined : 'What you call it, like "R34". Its TUNE MFD shows it.'}
        value={form.nickname}
        onChange={set('nickname')}
        maxLength={40}
        autoComplete="off"
        error={errors.nickname}
        data-autofocus
      />
      <div className={s.pair}>
        <TextField label="Make" optional value={form.make} onChange={set('make')} maxLength={60} autoComplete="off" />
        <TextField
          label="Model"
          optional
          value={form.model}
          onChange={set('model')}
          maxLength={60}
          autoComplete="off"
        />
      </div>
      <TextField
        label="Year"
        optional
        inputMode="numeric"
        value={form.year}
        onChange={set('year')}
        maxLength={4}
        autoComplete="off"
        error={errors.year}
        className={s.short}
      />
      {full && (
        <>
          <div className={s.pair}>
            <TextField
              label="Engine"
              optional
              value={form.engine}
              onChange={set('engine')}
              maxLength={60}
              autoComplete="off"
            />
            <TextField
              label="Transmission"
              optional
              value={form.transmission}
              onChange={set('transmission')}
              maxLength={60}
              autoComplete="off"
            />
          </div>
          <TextArea
            label="About it"
            optional
            hint='A line or two its MFD shows, like "Weekend track car".'
            rows={3}
            value={form.bio}
            onChange={set('bio')}
            maxLength={500}
          />
        </>
      )}
    </>
  );
}

function DetailsForm({
  id,
  start,
  full,
  failed,
  onSend,
}: {
  id: string;
  start: Details;
  full: boolean;
  failed: unknown;
  onSend: (body: DetailsBody) => void;
}) {
  const [form, setForm] = useState(start);
  const [errors, setErrors] = useState<Errors>({});
  const set = (key: keyof Details) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const read = readDetails(form);
    if ('errors' in read) {
      setErrors(read.errors);
      return;
    }
    setErrors({});
    onSend(read.body);
  };
  return (
    <form id={id} className={s.form} onSubmit={submit} noValidate>
      {failed !== null && failed !== undefined && <Banner tone="error">{problemOf(failed)}</Banner>}
      <DetailFields form={form} set={set} errors={errors} full={full} />
    </form>
  );
}

function problemOf(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  if (code === 'quota_exceeded') {
    return 'Your account has as many cars as it can. Delete one you no longer have to add another.';
  }
  return problem(error);
}

/** Adding a car: its name and what it is. Then the car opens, to sign in its MFD. */
export function AddCarSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const id = useId();
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const say = useToast();
  const idempotency = useIdempotencyKey();
  const add = useMutation({
    mutationFn: (body: VehicleIn) =>
      data(api.POST('/vehicles', { params: { header: { 'Idempotency-Key': idempotency.keyFor(body) } }, body })),
    onSuccess: (car) => {
      idempotency.made();
      queryClient.setQueryData(keys.vehicle(car.id), car);
      void queryClient.invalidateQueries({ queryKey: keys.vehicles });
      say(`${car.nickname} is in your garage.`);
      // In the sheet's place, so Back goes to where the sheet was opened from.
      void navigate(`/garage/${car.id}`, { replace: true });
    },
  });
  const close = () => {
    add.reset();
    onClose();
  };
  return (
    <Sheet open={open} onClose={close} title="Add a car" action={{ label: 'Add', form: id, busy: add.isPending }}>
      <DetailsForm id={id} start={NONE} full={false} failed={add.error} onSend={(body) => add.mutate(body)} />
      <p className={s.note}>
        Next, sign in its TUNE MFD. Its settings, trips and datalogs then come here by themselves.
      </p>
    </Sheet>
  );
}

/** Changing a car's details: its MFD shows them on its TUNE account page. */
export function EditCarSheet({ car, open, onClose }: { car: Vehicle; open: boolean; onClose: () => void }) {
  const id = useId();
  const save = useSaveCar(car, onClose);
  return (
    <Sheet
      open={open}
      onClose={() => {
        save.reset();
        onClose();
      }}
      title="About the car"
      action={{ label: 'Save', form: id, busy: save.isPending }}
    >
      <DetailsForm id={id} start={detailsOf(car)} full failed={save.error} onSend={(body) => save.mutate(body)} />
    </Sheet>
  );
}

function useSaveCar(car: Vehicle, done: () => void) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Patch) =>
      data(api.PATCH('/vehicles/{vehicleId}', { params: { path: { vehicleId: car.id } }, body: patch })),
    onSuccess: (saved) => {
      queryClient.setQueryData(keys.vehicle(car.id), saved);
      void queryClient.invalidateQueries({ queryKey: keys.vehicles });
      done();
    },
  });
}

/** The Analyser's figures, as typed: a number from `least` to `most`, or nothing. */
const FIGURES = [
  { key: 'weightKg', label: 'Weight (kg)', least: 1, most: 20000, hint: "As it's driven, with you in it." },
  { key: 'dragCoefficient', label: 'Drag coefficient (Cd)', least: 0.05, most: 2, hint: 'Like 0.32, from its maker.' },
  { key: 'frontalAreaM2', label: 'Frontal area (m²)', least: 0.3, most: 15, hint: 'Like 2.0, from its maker.' },
] as const;

type Figure = (typeof FIGURES)[number]['key'];

function shown(n: number, places: number): string {
  return n.toLocaleString('en-AU', { maximumFractionDigits: places });
}

/** What the Analyser's dyno and fuelling checks use. The MFD never sees these. */
export function EditAnalyserSheet({ car, open, onClose }: { car: Vehicle; open: boolean; onClose: () => void }) {
  const id = useId();
  const save = useSaveCar(car, onClose);
  return (
    <Sheet
      open={open}
      onClose={() => {
        save.reset();
        onClose();
      }}
      title="For the Analyser"
      action={{ label: 'Save', form: id, busy: save.isPending }}
    >
      <AnalyserForm id={id} car={car} failed={save.error} onSend={(patch) => save.mutate(patch)} />
    </Sheet>
  );
}

function AnalyserForm({
  id,
  car,
  failed,
  onSend,
}: {
  id: string;
  car: Vehicle;
  failed: unknown;
  onSend: (patch: Patch) => void;
}) {
  const typed = (n: number | null) => (n === null ? '' : String(n));
  const [form, setForm] = useState<Record<Figure, string>>({
    weightKg: typed(car.weightKg),
    dragCoefficient: typed(car.dragCoefficient),
    frontalAreaM2: typed(car.frontalAreaM2),
  });
  const [fuel, setFuel] = useState<Fuel | ''>(car.fuel ?? '');
  const [errors, setErrors] = useState<Partial<Record<Figure, string>>>({});
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found: Partial<Record<Figure, string>> = {};
    const values: Partial<Record<Figure, number | null>> = {};
    for (const f of FIGURES) {
      const text = form[f.key].trim().replace(/[\s,]/g, '');
      const n = text === '' ? null : Number(text);
      if (n !== null && (!Number.isFinite(n) || n < f.least || n > f.most)) {
        found[f.key] = `A number from ${shown(f.least, 2)} to ${shown(f.most, 2)}, or nothing.`;
      }
      values[f.key] = n;
    }
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }
    onSend({
      weightKg: values.weightKg ?? null,
      dragCoefficient: values.dragCoefficient ?? null,
      frontalAreaM2: values.frontalAreaM2 ?? null,
      fuel: fuel === '' ? null : fuel,
    });
  };
  return (
    <form id={id} className={s.form} onSubmit={submit} noValidate>
      {failed !== null && failed !== undefined && <Banner tone="error">{problem(failed)}</Banner>}
      <Select label="Fuel" value={fuel} onChange={(e) => setFuel(e.target.value as Fuel | '')} data-autofocus>
        <option value="">Not given</option>
        {FUELS.map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </Select>
      {FIGURES.map((f) => (
        <TextField
          key={f.key}
          label={f.label}
          optional
          inputMode="decimal"
          autoComplete="off"
          hint={f.hint}
          value={form[f.key]}
          onChange={(e) => setForm((all) => ({ ...all, [f.key]: e.target.value }))}
          error={errors[f.key]}
        />
      ))}
      <p className={s.note}>
        The Analyser's dyno and fuelling checks use these, and a typical car's figures for any not given. The MFD
        doesn't.
      </p>
    </form>
  );
}
